import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { UpdateContentApi } from '@/domains/proof-reading/apis/update-content-api';
import { ChapterOperations } from '../chapter-operations';
import type {
    ParagraphReplacementService,
    ReplaceParagraphParams,
    ParagraphReplacedEvent,
} from '../paragraph-replacement-service';

@injectable()
export class ParagraphReplacementServiceImpl implements ParagraphReplacementService {
    private readonly _onParagraphReplacedEmitter = new Emitter<ParagraphReplacedEvent>();
    readonly onParagraphReplaced = this._onParagraphReplacedEmitter.event;

    private readonly _onParagraphReplaceFailedEmitter = new Emitter<{
        params: ReplaceParagraphParams;
        error: Error;
    }>();
    readonly onParagraphReplaceFailed = this._onParagraphReplaceFailedEmitter.event;

    constructor(
        @inject(UpdateContentApi)
        private readonly _updateContentApi: UpdateContentApi,
        @inject(ChapterOperations)
        private readonly _chapterOperations: ChapterOperations,
    ) {}

    async replaceParagraph(params: ReplaceParagraphParams): Promise<void> {
        const { issue, content, taskId } = params;

        try {
            // 获取当前段落信息
            const currentParagraph = this._chapterOperations.getCurrentParagraph(issue.paragraphIndex);

            if (!currentParagraph) {
                throw new Error(`段落不存在: ${issue.paragraphIndex}`);
            }

            // 调用 API 更新内容
            await this._updateContentApi.updateContent({
                task_id: taskId,
                chapter_type: issue.chapterId,
                content,
                errors: issue.issues.map(issueItem => issueItem.id),
                split_index: currentParagraph.splitIndex,
                embodiment_number: currentParagraph.embodimentNumber,
                embodiment_type: currentParagraph.embodimentType,
            });

            // API 调用成功后，更新本地 chapters 数据
            this._chapterOperations.updateParagraphContent(
                issue.chapterId,
                issue.paragraphIndex,
                content,
            );

            // 触发成功事件
            this._onParagraphReplacedEmitter.fire({
                issue,
                content,
                taskId,
            });
        } catch (error) {
            console.warn('段落替换失败', error);

            // 触发失败事件
            this._onParagraphReplaceFailedEmitter.fire({
                params,
                error: error instanceof Error ? error : new Error(String(error)),
            });

            throw error;
        }
    }
}
